import { useState } from "react";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import type { FormFieldKind, FormSection } from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddFieldButton } from "./add-field-button";
import { FieldBlock } from "./field-block";

// Single section card — heading + collapsible field list. The parent
// FormBuilderCanvas owns the DndContext + per-section SortableContext so
// within-section drags reorder correctly; section reorder is out of scope
// (legacy didn't ship it).

export function SectionBlock({
  section,
  sectionIndex,
  canDelete,
  selectedFieldId,
  onSelectField,
  onUpdateSection,
  onDeleteSection,
  onAddField,
}: {
  section: FormSection;
  sectionIndex: number;
  canDelete: boolean;
  selectedFieldId: string | null;
  onSelectField: (id: string | null) => void;
  onUpdateSection: (patch: Partial<FormSection>) => void;
  onDeleteSection: () => void;
  onAddField: (kind: FormFieldKind, insertAfterFieldId?: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <section className="rounded-xl border bg-card">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand section" : "Collapse section"}
          className="text-muted-foreground hover:text-foreground"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
          Section {sectionIndex + 1}
        </span>
        <Input
          value={section.heading ?? ""}
          placeholder="Section heading (optional)"
          onChange={(e) =>
            onUpdateSection({ heading: e.target.value || null })
          }
          className="h-7 max-w-md border-0 bg-transparent px-1 font-display text-base font-medium focus-visible:ring-1"
        />
        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Delete section"
            disabled={!canDelete}
            onClick={onDeleteSection}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </header>

      {!collapsed && (
        <div className="space-y-1 px-4 py-3">
          {section.fields.length === 0 && (
            <div className="rounded-md border border-dashed bg-background/40 px-4 py-6 text-center text-xs text-muted-foreground">
              No fields yet — use the button below to add one.
            </div>
          )}

          {section.fields.map((field) => (
            <FieldBlock
              key={field.id}
              field={field}
              selected={selectedFieldId === field.id}
              onSelect={() => onSelectField(field.id)}
              onAddBelow={(kind) => onAddField(kind, field.id)}
            />
          ))}

          <div className="pt-2">
            <AddFieldButton onAdd={(kind) => onAddField(kind)} />
          </div>
        </div>
      )}
    </section>
  );
}
